// AuthContext.js - Authentication state management
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import config from '../config';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Cache user in sessionStorage to prevent loading flash on hot reload
  const cachedUser = sessionStorage.getItem('cached_user');
  const [user, setUser] = useState(cachedUser ? JSON.parse(cachedUser) : null);
  const [token, setToken] = useState(localStorage.getItem('auth_token'));
  // Skip loading state if we have cached user data (hot reload scenario)
  const [loading, setLoading] = useState(!cachedUser && !!localStorage.getItem('auth_token'));
  const [error, setError] = useState(null);

  // Fetch current user on mount or when token changes
  useEffect(() => {
    const fetchUser = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${config.API_BASE_URL}/auth/me`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          setUser(data.user);
          sessionStorage.setItem('cached_user', JSON.stringify(data.user));
        } else {
          // Token is invalid, clear it
          localStorage.removeItem('auth_token');
          sessionStorage.removeItem('cached_user');
          setToken(null);
          setUser(null);
        }
      } catch (err) {
        console.error('Error fetching user:', err);
        localStorage.removeItem('auth_token');
        sessionStorage.removeItem('cached_user');
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [token]);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const response = await fetch(`${config.API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        return { 
          success: false, 
          error: data.error || 'Login failed',
          action: data.action,  // Pass through the action hint (e.g., 'register')
          pending_approval: data.pending_approval  // Pass through pending approval status
        };
      }

      localStorage.setItem('auth_token', data.access_token);
      sessionStorage.setItem('cached_user', JSON.stringify(data.user));
      setToken(data.access_token);
      setUser(data.user);
      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  }, []);

  const register = useCallback(async (email, password, displayName) => {
    setError(null);
    try {
      const response = await fetch(`${config.API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password, display_name: displayName })
      });

      const data = await response.json();

      if (!response.ok) {
        return { 
          success: false, 
          error: data.error || 'Registration failed',
          action: data.action  // Pass through the action hint (e.g., 'login')
        };
      }

      // Check if registration is pending approval (no token provided)
      if (data.pending_approval) {
        return { 
          success: true, 
          pending_approval: true,
          message: data.message
        };
      }

      // Normal registration with immediate access
      if (data.access_token) {
        localStorage.setItem('auth_token', data.access_token);
        sessionStorage.setItem('cached_user', JSON.stringify(data.user));
        setToken(data.access_token);
        setUser(data.user);
      }
      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('auth_token');
    sessionStorage.removeItem('cached_user');
    setToken(null);
    setUser(null);
    setError(null);
  }, []);

  const getAuthHeaders = useCallback(() => {
    if (!token) return {};
    return {
      'Authorization': `Bearer ${token}`
    };
  }, [token]);

  const value = {
    user,
    token,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    getAuthHeaders
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;

